import { ReactNode, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { ProductTipsHost, TipProgressProvider } from 'productTips';
import { screeningClient } from 'screening/apiClient';
import { useJobs } from 'screening/queries';
import {
  resolveScreeningTips,
  screeningTipRegistry,
} from 'screening/tips/registry';

type Props = {
  children: ReactNode;
  /** When false, coachmarks do not auto-start (plans gate still open). */
  billingReady?: boolean;
};

const ScreeningTipsInner = ({ children, billingReady = true }: Props) => {
  const router = useRouter();
  const jobs = useJobs();
  const isOnboarding = router.pathname.startsWith('/screening/onboarding');

  const whenContext = useMemo(
    () => ({
      screening: {
        hasJobs: (jobs.data?.jobs?.length ?? 0) > 0,
        isOnboarding,
        billingReady,
      },
    }),
    [jobs.data?.jobs?.length, isOnboarding, billingReady]
  );

  return (
    <ProductTipsHost
      tokenPrefix="screening"
      registry={screeningTipRegistry}
      resolveVisible={resolveScreeningTips}
      whenContext={whenContext}
      allowAutoStart={!isOnboarding && billingReady && !jobs.isLoading}
    >
      {children}
    </ProductTipsHost>
  );
};

const ScreeningTips = ({ children, billingReady = true }: Props) => {
  const load = useCallback(() => screeningClient.getUiProgress(), []);
  const patch = useCallback(
    (payload: Record<string, unknown>) =>
      screeningClient.patchUiProgress(payload),
    []
  );

  return (
    <TipProgressProvider tokenPrefix="screening" load={load} patch={patch}>
      <ScreeningTipsInner billingReady={billingReady}>
        {children}
      </ScreeningTipsInner>
    </TipProgressProvider>
  );
};

export default ScreeningTips;
